/**
 * AssignPickerPopover.tsx
 * The skill-smart ＋Assign picker for a task staffing card (STAFF-VIEW-V2.md §3).
 * Candidates arrive already sorted QUALIFIED-FIRST then least-loaded; each row
 * shows the person's SkillBadge (their level for THIS task's skill) and their
 * current load ("lots free" / "at capacity"), so the planner can put a qualified,
 * un-overloaded person on the task in one glance + click.
 *
 * Colors via theme tokens — no hex literals (CODING-STANDARDS).
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { useTheme, alpha } from '@mui/material/styles';
import { SkillBadge } from './SkillBadge';
import { spareCapacityHint } from '../../../../utils/rosterAllocation';
import { allocationBand } from '../../../../utils/scheduleAllocationBands';
import type { StaffCandidate } from './staffSkillModel';

export interface AssignPickerPopoverProps {
  anchorEl: HTMLElement | null;
  candidates: StaffCandidate[];
  skillRequired: boolean;
  onPick: (userKey: string) => void;
  onClose: () => void;
}

export const AssignPickerPopover: React.FC<AssignPickerPopoverProps> = ({
  anchorEl,
  candidates,
  skillRequired,
  onPick,
  onClose,
}) => {
  const theme = useTheme();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? candidates.filter((c) => c.displayName.toLowerCase().includes(q)) : candidates;
  }, [candidates, query]);

  const loadColor = (percent: number): string => {
    const band = allocationBand(percent);
    return band === 'over' ? theme.schedule.allocOver
      : band === 'at' ? theme.schedule.allocAt
        : band === 'healthy' ? theme.schedule.allocHealthy
          : theme.schedule.allocUnder;
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableScrollLock
      PaperProps={{ sx: { width: 320, borderRadius: '10px', border: `1px solid ${theme.border.color}` } }}
    >
      <Box sx={{ p: 1 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.5 } }}
        />
        <Typography variant="caption" sx={{ display: 'block', px: 0.5, pt: 0.75, pb: 0.25, fontSize: '0.62rem', color: 'text.secondary' }}>
          {skillRequired ? 'Qualified people first, then who has room.' : 'This task needs no skill — sorted by who has room.'}
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 300, overflowY: 'auto', pb: 0.5 }}>
        {filtered.length === 0 ? (
          <Typography variant="caption" sx={{ display: 'block', px: 1.5, py: 1.5, fontSize: '0.72rem', color: 'text.disabled' }}>
            No one left to add.
          </Typography>
        ) : (
          filtered.map((c) => (
            <Box
              key={c.userKey}
              role="button"
              tabIndex={0}
              onClick={() => onPick(c.userKey)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(c.userKey); } }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.25,
                py: 0.6,
                cursor: 'pointer',
                '&:hover': { backgroundColor: alpha(theme.schedule.employeeAlloc, 0.1) },
              }}
            >
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.displayName}
              </Typography>
              <SkillBadge level={c.level} skillRequired={skillRequired} size="sm" />
              <Box sx={{ textAlign: 'right', minWidth: 56 }}>
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: loadColor(c.allocationPercent), lineHeight: 1.1 }}>
                  {c.allocationPercent > 0 ? `${Math.round(c.allocationPercent)}%` : 'free'}
                </Typography>
                <Typography sx={{ fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1.1 }}>
                  {spareCapacityHint(c.allocationPercent)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Popover>
  );
};
